// frontend/src/api/workflowAdmin.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-06 10:28 BST
//
// Description:
//   Frontend API helper for workflow template admin surfaces.
//
//   Responsibilities:
//     - Wrap workflow template CRUD routes
//     - Wrap workflow template version routes
//     - Wrap workflow template step CRUD routes
//     - Wrap workflow template step action CRUD routes
//     - Wrap workflow template binding CRUD routes
//     - Wrap workflow template category CRUD routes
//     - Wrap taxonomy lookup routes used by workflow bindings
//     - Keep URL construction consistent with axiosInstance
//
//   Important:
//     - Uses canonical axiosInstance baseURL handling
//     - No business logic here
//     - Phase 4 starts with templates + versions + steps + step actions + bindings
//
// Change Log (GMT/BST):
//   2026-10-06 10:28 BST - Phase 1C.3C: added the dedicated Work Package taxonomy
//                           tree lookup used by dual-domain workflow binding admin.

import api from "./axiosInstance";

export async function listWorkflowTemplates(params = {}) {
  const res = await api.get("/workflow-templates", { params });
  return res.data;
}

export async function createWorkflowTemplate(payload) {
  const res = await api.post("/workflow-templates", payload);
  return res.data;
}

export async function getWorkflowTemplate(workflowTemplateId) {
  const res = await api.get(`/workflow-templates/${workflowTemplateId}`);
  return res.data;
}

export async function updateWorkflowTemplate(workflowTemplateId, payload) {
  const res = await api.put(`/workflow-templates/${workflowTemplateId}`, payload);
  return res.data;
}

export async function archiveWorkflowTemplate(workflowTemplateId) {
  const res = await api.delete(`/workflow-templates/${workflowTemplateId}`);
  return res.data;
}

export async function listWorkflowTemplateVersions(workflowTemplateId, params = {}) {
  const res = await api.get(`/workflow-templates/${workflowTemplateId}/versions`, { params });
  return res.data;
}

export async function getWorkflowTemplateVersion(workflowTemplateId, workflowTemplateVersionId) {
  const res = await api.get(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}`
  );
  return res.data;
}

export async function createWorkflowTemplateDraft(workflowTemplateId, payload = {}) {
  const res = await api.post(`/workflow-templates/${workflowTemplateId}/versions/draft`, payload);
  return res.data;
}

export async function publishWorkflowTemplateVersion(
  workflowTemplateId,
  workflowTemplateVersionId,
  payload = {}
) {
  const res = await api.post(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/publish`,
    payload
  );
  return res.data;
}

export async function listWorkflowTemplateSteps(workflowTemplateId, workflowTemplateVersionId) {
  const res = await api.get(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps`
  );
  return res.data;
}

export async function getWorkflowTemplateStep(
  workflowTemplateId,
  workflowTemplateVersionId,
  workflowTemplateStepId
) {
  const res = await api.get(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps/${workflowTemplateStepId}`
  );
  return res.data;
}

export async function createWorkflowTemplateStep(
  workflowTemplateId,
  workflowTemplateVersionId,
  payload
) {
  const res = await api.post(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps`,
    payload
  );
  return res.data;
}

export async function updateWorkflowTemplateStep(
  workflowTemplateId,
  workflowTemplateVersionId,
  workflowTemplateStepId,
  payload
) {
  const res = await api.put(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps/${workflowTemplateStepId}`,
    payload
  );
  return res.data;
}

export async function deleteWorkflowTemplateStep(
  workflowTemplateId,
  workflowTemplateVersionId,
  workflowTemplateStepId
) {
  const res = await api.delete(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps/${workflowTemplateStepId}`
  );
  return res.data;
}

export async function listWorkflowTemplateStepActions(
  workflowTemplateId,
  workflowTemplateVersionId,
  workflowTemplateStepId
) {
  const res = await api.get(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps/${workflowTemplateStepId}/actions`
  );
  return res.data;
}

export async function getWorkflowTemplateStepAction(
  workflowTemplateId,
  workflowTemplateVersionId,
  workflowTemplateStepId,
  workflowTemplateStepActionId
) {
  const res = await api.get(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps/${workflowTemplateStepId}/actions/${workflowTemplateStepActionId}`
  );
  return res.data;
}

export async function createWorkflowTemplateStepAction(
  workflowTemplateId,
  workflowTemplateVersionId,
  workflowTemplateStepId,
  payload
) {
  const res = await api.post(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps/${workflowTemplateStepId}/actions`,
    payload
  );
  return res.data;
}

export async function updateWorkflowTemplateStepAction(
  workflowTemplateId,
  workflowTemplateVersionId,
  workflowTemplateStepId,
  workflowTemplateStepActionId,
  payload
) {
  const res = await api.put(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps/${workflowTemplateStepId}/actions/${workflowTemplateStepActionId}`,
    payload
  );
  return res.data;
}

export async function deleteWorkflowTemplateStepAction(
  workflowTemplateId,
  workflowTemplateVersionId,
  workflowTemplateStepId,
  workflowTemplateStepActionId
) {
  const res = await api.delete(
    `/workflow-templates/${workflowTemplateId}/versions/${workflowTemplateVersionId}/steps/${workflowTemplateStepId}/actions/${workflowTemplateStepActionId}`
  );
  return res.data;
}

export async function listWorkflowTemplateBindings(params = {}) {
  const res = await api.get("/workflow-template-bindings", { params });
  return res.data;
}

export async function getWorkflowTemplateBinding(workflowTemplateBindingId) {
  const res = await api.get(`/workflow-template-bindings/${workflowTemplateBindingId}`);
  return res.data;
}

export async function createWorkflowTemplateBinding(payload) {
  const res = await api.post("/workflow-template-bindings", payload);
  return res.data;
}

export async function updateWorkflowTemplateBinding(workflowTemplateBindingId, payload) {
  const res = await api.put(`/workflow-template-bindings/${workflowTemplateBindingId}`, payload);
  return res.data;
}

export async function deactivateWorkflowTemplateBinding(workflowTemplateBindingId, payload = {}) {
  const res = await api.post(
    `/workflow-template-bindings/${workflowTemplateBindingId}/deactivate`,
    payload
  );
  return res.data;
}

export async function listWorkflowTemplateCategories(params = {}) {
  const res = await api.get("/workflow-template-categories", { params });
  return res.data;
}

export async function getWorkflowTemplateCategory(workflowTemplateCategoryId) {
  const res = await api.get(`/workflow-template-categories/${workflowTemplateCategoryId}`);
  return res.data;
}

export async function createWorkflowTemplateCategory(payload) {
  const res = await api.post("/workflow-template-categories", payload);
  return res.data;
}

export async function updateWorkflowTemplateCategory(workflowTemplateCategoryId, payload) {
  const res = await api.put(
    `/workflow-template-categories/${workflowTemplateCategoryId}`,
    payload
  );
  return res.data;
}

export async function deactivateWorkflowTemplateCategory(
  workflowTemplateCategoryId,
  payload = {}
) {
  const res = await api.post(
    `/workflow-template-categories/${workflowTemplateCategoryId}/deactivate`,
    payload
  );
  return res.data;
}

export async function getTaxonomyTree(params = {}) {
  const res = await api.get("/taxonomy/tree", { params });
  return res.data;
}

export async function getWorkPackageTaxonomyTree(params = {}) {
  const res = await api.get("/work-package-taxonomy/tree", { params });
  return res.data;
}